import RelativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import flatten from "lodash/flatten";
import isURL from "validator/lib/isURL";
import log from "loglevel";

dayjs.extend(RelativeTime);

export const dateToText = (dateInSeconds) => {
  const minutes = Math.floor(dateInSeconds / 60);
  const seconds = Math.floor(dateInSeconds % 60);

  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const formattedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

  return `${formattedMinutes}:${formattedSeconds}`;
};

export const convertToValidUrl = (url) => {
  return url.startsWith("http://") || url.startsWith("https://")
    ? url
    : `https://${url}`;
};

export const minutesToString = (minutes) => {
  const minutesPastHour = minutes % 60;
  const hours = Math.floor(minutes / 60);
  let response = `${minutesPastHour}min`;
  if (hours > 0) {
    response = `${hours}hr ${response}`;
  }
  return response;
};

// 00:00:02 => 2
export const transcriptTimestampToSeconds = (timestamp) => {
  const timestampParts = timestamp.split(":");
  const response =
    parseInt(timestampParts[0]) * 3600 +
    parseInt(timestampParts[1]) * 60 +
    parseInt(timestampParts[2]);
  return response;
};

export const secondsToMinutes = (seconds) => {
  return Math.ceil(seconds / 60);
};

export const secondsToString = (seconds) => {
  if (seconds === null || seconds === undefined) {
    return "";
  }

  const minutes = secondsToMinutes(seconds);
  return minutesToString(minutes);
};

export const sourcingToolOptions = [
  {
    label: "No sourcing tool",
    value: "none",
  },
  {
    label: "LinkedIn Recruiter",
    value: "linkedin",
  },
  {
    label: "Covey",
    value: "covey",
  },
  {
    label: "Fetcher",
    value: "fetcher",
  },
  {
    label: "Gem",
    value: "gem",
  },
  {
    label: "Greenhouse interview confirmation",
    value: "greenhouse-interview-confirmation",
  },
  {
    label: "HireEZ",
    value: "hire-ez",
  },
  {
    label: "Lever candidate email",
    value: "lever-candidate-email",
  },
  {
    label: "Mixmax",
    value: "mixmax",
  },
  {
    label: "Paradox",
    value: "paradox",
  },
  {
    label: "Ashby",
    value: "ashby",
  },
];

export const getSourcingLinkWithParams = (url, sourcingTool) => {
  let urlWithParams = url;
  switch (sourcingTool) {
    case "gem":
      urlWithParams += `?p-fn={{first_name}}&p-ln={{last_name}}`;
      break;
    case "hire-ez":
      urlWithParams += `?p-fn={{%firstname%}}&p-ln={{%lastname%}}`;
      break;
    case "fetcher":
      urlWithParams += `?p-fn={{candidate_first_name}}&p-ln={{candidate_last_name}}`;
      break;
    case "covey":
      urlWithParams += `?p-fn={candidate_first_name}&p-ln={candidate_last_name}`;
      break;
    case "mixmax":
      urlWithParams += `?p-fn={{first name}}&p-ln={{last name}}`;
      break;
    case "linkedin":
      urlWithParams += `?p-fn={firstName}&p-ln={lastName}`;
      break;
    case "lever-candidate-email":
      urlWithParams += `?p-un={{Candidate full name}}&p-fn={{Candidate first name}}&p-jt={{Job posting title}}`;
      break;
    case "greenhouse-interview-confirmation":
      urlWithParams += `?p-e={{CANDIDATE_EMAIL_ADDRESS}}&p-fn={{CANDIDATE_FIRST_NAME}}&p-un={{CANDIDATE_NAME}}&p-jt={{JOB_NAME}}`;
      break;
    case "paradox":
      urlWithParams += `?p-e=#email&p-fn=#candidate-firstname&p-ln=#candidate-lastname&p-jt=#job-title`;
      break;
    case "ashby":
      urlWithParams += `?p-e={primaryPersonalEmail}&p-un={candidateFullName}`;
      urlWithParams = `{${urlWithParams}}`;
      break;
    default:
      break;
  }
  return urlWithParams;
};

export const flattenTree = (roots, childrenFunc) => {
  if (!roots || roots.length === 0) return [];
  const flattenedTree = roots
    ? flattenTree(
        flatten(
          roots.map((root) => {
            return childrenFunc(root).map((c) => {
              return {
                ...c,
                parent: root,
              };
            });
          })
        ),
        childrenFunc
      )
    : [];
  return [...roots, ...flattenedTree];
};

export const formatSalary = (
  salary,
  shouldShowHourlySalary = false,
  shouldRound = false
) => {
  if (shouldShowHourlySalary) {
    return `$${shouldRound ? Math.round(salary) : salary.toFixed(2)}`;
  } else {
    return `$${Math.floor(salary / 1000)}k`;
  }
};

export const formatSalaryRange = (
  lowerSalary,
  upperSalary,
  shouldShowHourlySalary = false,
  shouldRound = false
) => {
  let salaryString = formatSalary(
    lowerSalary,
    shouldShowHourlySalary,
    shouldRound
  );
  if (lowerSalary !== upperSalary) {
    salaryString += `-${formatSalary(
      upperSalary,
      shouldShowHourlySalary,
      shouldRound
    )}`;
  }
  if (shouldShowHourlySalary) {
    salaryString += "/hour";
  }
  return salaryString;
};

// Stolen from here: https://github.com/granteagon/move/blob/master/src/index.js
export const move = (array, moveIndex, toIndex) => {
  /* #move - Moves an array item from one position in an array to another.
     Note: This is a pure function so a new array will be returned, instead
     of altering the array argument.
    Arguments:
    1. array     (String) : Array in which to move an item.         (required)
    2. moveIndex (Object) : The index of the item to move.          (required)
    3. toIndex   (Object) : The index to move item at moveIndex to. (required)
  */
  const item = array[moveIndex];
  const length = array.length;
  const diff = moveIndex - toIndex;

  if (diff > 0) {
    // move left
    return [
      ...array.slice(0, toIndex),
      item,
      ...array.slice(toIndex, moveIndex),
      ...array.slice(moveIndex + 1, length),
    ];
  } else if (diff < 0) {
    // move right
    const targetIndex = toIndex + 1;
    return [
      ...array.slice(0, moveIndex),
      ...array.slice(moveIndex + 1, targetIndex),
      item,
      ...array.slice(targetIndex, length),
    ];
  }
  return array;
};

// Stolen from here: https://stackoverflow.com/questions/3942878/how-to-decide-font-color-in-white-or-black-depending-on-background-color
export const shouldUseDarkText = (bgColor) => {
  var color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
  var r = parseInt(color.substring(0, 2), 16); // hexToR
  var g = parseInt(color.substring(2, 4), 16); // hexToG
  var b = parseInt(color.substring(4, 6), 16); // hexToB
  var uicolors = [r / 255, g / 255, b / 255];
  var c = uicolors.map((col) => {
    if (col <= 0.03928) {
      return col / 12.92;
    }
    return Math.pow((col + 0.055) / 1.055, 2.4);
  });
  var L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return L > 0.179 ? true : false;
};

export const getImageUrl = (url, type, size) => {
  if (
    !url ||
    url.startsWith("data:") ||
    url.endsWith(".svg") ||
    url.endsWith(".ico") ||
    url.startsWith("/static/") ||
    url.startsWith("https://static.careerpuck.com/")
  ) {
    return url;
  }

  const baseUrlArray = url.split(".");
  baseUrlArray.pop();

  if (size) {
    baseUrlArray.push(size);
  }
  if (type === "png") {
    baseUrlArray.push("png");
  } else if (type === "jpg") {
    baseUrlArray.push("jpg");
  }

  return baseUrlArray.join(".");
};

export const matchJobsToSearchTerm = (searchTerm, jobs) => {
  const substitutions = [
    ["senior", "sr"],
    ["junior", "jr"],
    ["csm", "customer success manager"],
    ["manager", "mgr"],
    ["human resources", "hr"],
    ["operations", "ops"],
    ["ae", "account executive"],
    ["ml", "machine learning"],
  ];

  function removePunctuation(text) {
    return text ? text.replace(/[^\w\s\d]/g, "") : "";
  }

  const cleanSearchTerm = removePunctuation(searchTerm.toLowerCase());

  function matchField(searchPattern, field) {
    const cleanField = removePunctuation(field);
    return cleanField && cleanField.toLowerCase().search(searchPattern) > -1;
  }

  function matchFieldWithSubstitutions(searchTerm, field) {
    let searchPattern = searchTerm;
    substitutions.forEach((substitution) => {
      const substitutionPattern = `(${substitution[0]}|${substitution[1]})`;
      const substitutionRegexp = new RegExp(substitutionPattern, "g");

      searchPattern = searchPattern.replace(
        substitutionRegexp,
        substitutionPattern
      );
    });

    if (matchField(searchPattern, field)) {
      return true;
    }

    return false;
  }

  if (cleanSearchTerm.length > 0) {
    // Filter jobs by the search term matching the job title, the location, the department
    let filteredJobs = jobs.filter((job) => {
      const titleMatch = matchFieldWithSubstitutions(
        cleanSearchTerm,
        job.title
      );
      const locationMatch = matchFieldWithSubstitutions(
        cleanSearchTerm,
        job.location
      );
      const departmentMatch = matchFieldWithSubstitutions(
        cleanSearchTerm,
        job.topDepartment?.name
      );
      return titleMatch || locationMatch || departmentMatch;
    });
    return filteredJobs;
  }

  return false;
};

export const isValidLinkedInUrl = (url) => {
  let isValid = false;
  if (isURL(url) && url.includes("linkedin.com")) {
    isValid = true;
  }
  return isValid;
};

export const isValidName = (name) => {
  // Define the regular expression pattern to exclude @
  const invalidCharPattern = /@/;

  // Test the name against the pattern
  return !invalidCharPattern.test(name);
};

// Helper function to convert object keys to lowercase
export const transformKeysToLowerCase = (obj) => {
  const newObj = {};
  Object.keys(obj).forEach((key) => {
    newObj[key.toLowerCase()] = obj[key];
  });
  return newObj;
};

// For converting from email/phone to contact infos
export const convertCsvPersonsToPayload = (csvPersons) => {
  // Convert email/phone to contact infos
  let csvPersonsPayload = csvPersons.map((person) => {
    let contactInfos = [];
    if (person.email) {
      contactInfos.push({
        type: "email",
        value: person.email,
        isPrimary: true,
      });
    }
    if (person.phone) {
      contactInfos.push({
        type: "phone",
        value: person.phone,
        isPrimary: true,
      });
    }

    return {
      firstName: person.firstName,
      lastName: person.lastName,
      contactInfos,
      linkedInProfileId: person.linkedInProfileId,
    };
  });
  return csvPersonsPayload;
};

export const getFilterOptionsFromResults = (
  filterType,
  inputValue,
  results
) => {
  // log.debug("The autocomplete results are: ", results);

  let options = [];

  // Convert to select option format
  const resultsOptions = results.map((result) => ({
    label: result.value,
    value: result,
  }));

  // Add special logic for job statuses, for now
  const isGrouped = results.some(
    (result) => result["personProfile.applicationHistory.atsJobStatus"]
  );

  // log.debug("isGrouped: ", isGrouped);

  let groupedOptions = [];
  if (isGrouped) {
    // Find active job results
    const activeResults = resultsOptions.filter(
      (result) =>
        result.value["personProfile.applicationHistory.atsJobStatus"] ===
          "public" ||
        result.value["personProfile.applicationHistory.atsJobStatus"] ===
          "private"
    );
    const inactiveResults = resultsOptions.filter(
      (result) =>
        result.value["personProfile.applicationHistory.atsJobStatus"] !==
          "public" &&
        result.value["personProfile.applicationHistory.atsJobStatus"] !==
          "private"
    );

    // Add active and inactive options
    groupedOptions.push({
      label: "Active jobs",
      options: [...activeResults],
    });
    groupedOptions.push({
      label: "Inactive jobs",
      options: [...inactiveResults],
    });

    // log.debug("The grouped options are: ", groupedOptions);
  }

  // log.debug(resultsOptions);

  if (inputValue) {
    // Add an "all" option
    options = [
      {
        options: [
          {
            label: `All ${filterType} with "${inputValue}"`,
            value: {
              value: inputValue,
            },
            isAllOption: true,
          },
        ],
      },
    ];

    // Only show options if there are results, otherwise there's a divider with no options
    if (resultsOptions.length) {
      if (isGrouped) {
        options = [...options, ...groupedOptions];
      } else {
        options = [...options, ...resultsOptions];
      }
    }
  } else {
    // options = resultsOptions;

    if (isGrouped) {
      options = [...groupedOptions];
    } else {
      options = resultsOptions;
    }
  }

  // log.debug("The autocomplete options are: ", options);

  return options;
};

export const blendWithWhite = (hexColor, opacity) => {
  // Ensure the hex color starts with '#'
  if (!hexColor.startsWith("#")) {
    throw new Error('Invalid hex color format. It should start with "#"');
  }

  // Remove the '#' and parse the hex color
  const hex = hexColor.slice(1);

  // Support short hex colors (#RGB)
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;

  if (fullHex.length !== 6) {
    throw new Error(
      "Invalid hex color format. It should be 3 or 6 characters long."
    );
  }

  // Extract RGB values
  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);

  // Blend with white background
  const blend = (foreground, background, alpha) =>
    Math.round(foreground * alpha + background * (1 - alpha));

  const blendedR = blend(r, 255, opacity);
  const blendedG = blend(g, 255, opacity);
  const blendedB = blend(b, 255, opacity);

  // Return the resulting color as a hex value
  return `#${blendedR.toString(16).padStart(2, "0")}${blendedG
    .toString(16)
    .padStart(2, "0")}${blendedB.toString(16).padStart(2, "0")}`;
};

export const pluralize = (singularWord, pluralWord, number) => {
  const num = Number(number);

  // Check if num is a valid number and exactly 1
  return !isNaN(num) && num === 1 ? singularWord : pluralWord;
};

export const getFirstLetter = (str) => {
  const match = str.match(/\p{L}/u); // Find the first Unicode letter
  return match ? match[0] : "";
};

export const getInitials = (firstName, lastName, email) => {
  let initials = "";
  if (firstName) {
    initials += getFirstLetter(firstName);
    if (lastName) {
      initials += getFirstLetter(lastName);
    }
  } else if (email) {
    initials = getFirstLetter(email);
  }

  return initials;
};

export const capitalize = (word) => {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const formatDuration = (seconds) => {
  if (seconds < 60) {
    return seconds === 1 ? "a second" : `${seconds} seconds`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? "a minute" : `${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "an hour" : `${hours} hours`;
  }

  const days = Math.floor(hours / 24);
  return days === 1 ? "a day" : `${days} days`;
};

export const formatAppliedDate = (date) => {
  // Handle null/undefined dates
  if (!date) {
    return "";
  }

  // If it's longer than 1 month ago, show the date
  let appliedString = "";
  if (dayjs().diff(date, "month") > 1) {
    appliedString += `${dayjs(date).format("MMM D, YYYY")} `;
  } else {
    appliedString += `${dayjs().to(date, true)} ago `;
  }

  return appliedString;
};

export const formatPlayCount = (num) => {
  // If less than 1000, use local formatting
  if (num < 1000) {
    return num.toLocaleString();
  }

  // Define suffixes and their values
  const suffixes = [
    { value: 1e9, suffix: "B" },
    { value: 1e6, suffix: "M" },
    { value: 1e3, suffix: "k" },
  ];

  // Find the appropriate suffix
  for (const { value, suffix } of suffixes) {
    if (num >= value) {
      const formatted = num / value;

      // If it's a whole number, don't show decimals
      if (formatted % 1 === 0) {
        return formatted + suffix;
      }

      // Show 1 decimal place, but remove trailing zeros
      return parseFloat(formatted.toFixed(1)) + suffix;
    }
  }

  // Fallback (shouldn't reach here)
  return num.toLocaleString();
};
